<template>
  <div class="abtest-form-page">
    <AbtestStepper
      :game-id="gameId"
      :version-id="versionId"
      :abtest-id="abtestId"
      :abtest="abtest"
      :in-wip-version="inWipVersion"
      :loading="queryLoading"
    >
      <VdForm :form="form" :rules="formRules" :before-cancel="beforeCancel" @submit="saveForm" @cancel="onFormCancel">
        <section class="main-info-section">
          <h2 class="section-title">Main Info</h2>

          <VdFormField
            v-model="form.name"
            prop="name"
            label="Name"
            type="text"
            show-word-limit
            max-length="50"
            placeholder="ex: Ad Freq USA vs. China"
            class="name-field"
            style="margin-bottom: 1.5rem"
          />
          <VdFormField
            v-model="form.description"
            prop="description"
            label="Description"
            type="textarea"
            placeholder="ex: need to know the best AdFreq"
            show-word-limit
            max-length="150"
            :auto-size="{ minRows: 5, maxRows: 10 }"
            class="description-field"
            :disabled="isRunningABTest"
          />

          <section class="layers-section">
            <VdFormField prop="layerId" label="Layer" placeholder="Choose the layer of the AB Test" class="layer-field">
              <AbtestLayerSelect
                v-model="form.layerId"
                :game-id="gameId"
                :version-id="versionId"
                :disabled="(!!abtest && abtest.cohorts.length > 1) || isRunningABTest"
              />
            </VdFormField>
          </section>
        </section>

        <section class="ab-test-type-section">
          <h2 class="section-title">AB test type</h2>
          <SelectButton
            v-model="selectedAbType"
            :disabled="!isWIP || !!abtestId"
            :options="abTestTypes"
            :allow-empty="false"
            aria-labelledby="basic"
          />
          <p v-if="!form.isDynamic">
            Static A/B tests in VoodooTune ensure players stay in the same segment throughout the test. These are the
            default A/B tests you're familiar with.
            <a
              href="https://www.notion.so/voodoo/AB-Test-on-VoodooTune-113a0b481db4801ebcdaf4716bc9a017"
              target="_blank"
            >
              More info
            </a>
          </p>
          <p v-else>
            Dynamic A/B tests allow players to move across segments within the same cohort during the A/B Test.
            <a href="https://www.notion.so/voodoo/Dynamic-AB-tests-114a0b481db4800b9061eaeac4129b93" target="_blank">
              More info
            </a>
          </p>
        </section>

        <section class="targeted-audience-section">
          <h2 class="section-title">Targeted audience</h2>

          <div>
            <VdFormField
              v-if="!form.isDynamic"
              prop="segmentIds"
              label="Choose existing segment(s)"
              class="segments-field targeted-segments"
            >
              <SegmentsSelectInput
                v-model="form.segmentIds"
                multiple
                :game-id="gameId"
                :version-id="versionId"
                :disabled="isRunningABTest"
              />
            </VdFormField>
            <div v-else>
              <VdFormField prop="segmentIds" label="Assignment segments" class="segments-field targeted-segments">
                <SegmentsSelectInput
                  v-model="form.segmentIds"
                  multiple
                  :game-id="gameId"
                  :version-id="versionId"
                  :disabled="isRunningABTest"
                />
              </VdFormField>
              <VdFormField prop="dynamicSegmentIds" label="Dynamic segments" class="segments-field">
                <SegmentsSelectInput
                  :model-value="form.dynamicSegmentIds"
                  multiple
                  :game-id="gameId"
                  :version-id="versionId"
                  :disabled="isRunningABTest"
                  @update:model-value="(value) => onSegmentUpdate(value)"
                />
              </VdFormField>
            </div>
          </div>

          <section v-if="canUserEditPlatform" class="platform-versions">
            <div class="platform">
              <VdSwitch v-model="form.ios" :disabled="isRunningABTest" />
              <div class="name">iOS</div>
              <div v-if="form.ios" class="game-version" :class="{ locked: isRunningABTest }">
                <VdFormField prop="iosGameVersionFrom" label="Game version:" inline>
                  <VdInputVersion v-model="form.iosGameVersionFrom" placeholder="earliest" prefix="from" />
                </VdFormField>
                <VdFormField prop="iosGameVersionTo" inline>
                  <VdInputVersion v-model="form.iosGameVersionTo" placeholder="latest" prefix="to" />
                </VdFormField>
              </div>
            </div>

            <div class="platform">
              <VdSwitch v-model="form.android" :disabled="isRunningABTest" />
              <div class="name">Android</div>
              <div v-if="form.android" class="game-version" :class="{ locked: isRunningABTest }">
                <VdFormField prop="androidGameVersionFrom" label="Game version:" inline>
                  <VdInputVersion v-model="form.androidGameVersionFrom" placeholder="earliest" prefix="from" />
                </VdFormField>
                <VdFormField prop="androidGameVersionTo" inline>
                  <VdInputVersion v-model="form.androidGameVersionTo" placeholder="latest" prefix="to" />
                </VdFormField>
              </div>
            </div>
          </section>
        </section>

        <section v-show="!isCreatingIapAbTest" class="advanced-settings-section">
          <h2 class="section-title">Advanced settings</h2>
          <VdFormField prop="beginsAt" label="Start - End dates">
            <el-date-picker
              :model-value="form.beginsAt"
              type="datetime"
              placeholder="Start date and time"
              :default-time="new Date(2000, 1, 1, 0, 0, 0)"
              :disabled="isRunningABTest"
              @update:model-value="(value) => onDateChanged('beginsAt', value)"
            />
            <span class="to">to</span>
            <el-date-picker
              :model-value="form.endsAt"
              type="datetime"
              placeholder="end date and time"
              :default-time="new Date(2000, 1, 1, 23, 59, 59)"
              :disabled="isRunningABTest"
              @update:model-value="(value) => onDateChanged('endsAt', value)"
            />
          </VdFormField>
        </section>

        <template #actions="{ onSubmit, onCancel }">
          <section class="abtest-actions">
            <VdButton
              type="secondary"
              skin="blue"
              :loading="(saveLoading || renameLoading) && saveAndBackToList && createLoading"
              @click="() => onSaveAndBackToList(onSubmit)"
            >
              Save &amp; back to list
            </VdButton>
            <div class="step-controls">
              <VdButton @click="onCancel">Cancel</VdButton>
              <VdButton
                type="primary"
                :loading="(saveLoading || renameLoading) && !saveAndBackToList"
                @click="onSubmit"
              >
                Next
              </VdButton>
            </div>
          </section>
        </template>
      </VdForm>
    </AbtestStepper>
  </div>
</template>

<script setup lang="ts">
import { FormRule, VdButton, VdForm, VdFormField, VdInputVersion, VdSwitch } from "@voodoo.io/voodoo-ui";
import compareVersions from "compare-versions";
import { parseJSON } from "date-fns";
import { computed, onMounted, reactive, ref, watch } from "vue";
import { RouteLocationRaw, useRoute, useRouter } from "vue-router";

import AbtestStepper from "@/components/AbtestStepper/AbtestStepper.vue";
import SegmentsSelectInput from "@/components/SegmentsSelectInput/SegmentsSelectInput.vue";
import {
  AbtestInput,
  AbtestState,
  AbtestType,
  Platform,
  useAbtestFormPageQuery,
  useAbtestFormPageSaveMutation,
  useIapAbtestFormPageSaveMutation,
  useMutationResponse,
} from "@/graphql";
import useFormTracking from "@/hooks/form-tracking";
import useMessages from "@/hooks/messages";
import useNotifications from "@/hooks/notifications";
import links from "@/router/links";
import SelectButton from "primevue/selectbutton";

import useBackendClient from "@/client";
import AbtestLayerSelect from "../components/AbtestLayerSelect/AbtestLayerSelect.vue";

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },

  versionId: {
    type: String,
    required: true,
  },

  /**
   * If the abtest is present, fill the form and update on next, create it otherwise.
   */
  abtestId: {
    type: String,
    default: null,
  },

  selectedLayer: {
    type: String,
    default: null,
  },
});

const router = useRouter();
const route = useRoute();
const { confirm } = useMessages();
const { notifySuccess, notifyError } = useNotifications();

const abTestTypes = ref(["Static", "Dynamic"]);

/**
 * Get the backend client
 */
const { client } = useBackendClient();

const isAllowedToUseShops = ref<boolean>(false);

/**
 * If we have a abtestId in props, then fetch it.
 */
const { result, loading: queryLoading } = useAbtestFormPageQuery(
  {
    gameId: props.gameId,
    versionId: props.versionId,
    abtestId: props.abtestId,
  },
  { fetchPolicy: "network-only" }
);

const cohorts = computed(() => result.value?.game?.version?.currentAbtest?.cohorts ?? []);
const abtest = computed(() => result.value?.game?.version?.abtest ?? undefined);
const layers = computed(() => result.value?.game?.version?.layers ?? []);
const isWIP = computed(() => props.versionId === "wip");
const inWipVersion = computed(() => result.value?.game?.version?.isWip ?? false);

const isRunningABTest = computed(
  () => !!abtest.value && [AbtestState.Published, AbtestState.Terminating].includes(abtest.value.state)
);

const isCreatingIapAbTest = route.query.iap && !props.abtestId;

const canUserEditPlatform = computed(() => !isCreatingIapAbTest);

/**
 * Check if the user has the permission to use the shop A/B test
 */
const checkAbTestTypesPermission = async () => {
  try {
    const response = await client.get<{
      allowedTypes: AbtestType[];
    }>(`/app/${props.gameId}/version/${props.versionId}/ab-tests/allowedAbTests`);
    const { allowedTypes } = response.data;
    isAllowedToUseShops.value = allowedTypes?.some((item) => AbtestType.Shop === item);
  } catch (err) {
    console.error(err);
    isAllowedToUseShops.value = false;
  }
};

onMounted(async () => {
  if (isCreatingIapAbTest && !window.history.state.selectedParentIaps?.length) {
    router.push({
      name: links.iap.iapHome,
      params: {
        gameId: props.gameId,
      },
      replace: true,
    });
  }
  checkAbTestTypesPermission();
});

/**
 * Define the form
 */
const form = reactive<
  AbtestInput & {
    beginsAt: string;
    endsAt: string;
    isShopAbTest: boolean;
  }
>({
  name: "",
  description: "",
  layerId: props.selectedLayer || "",
  beginsAt: "",
  endsAt: "",
  priority: 0,
  isImmutable: false,
  isDynamic: false,
  segmentIds: [],
  dynamicSegmentIds: [],
  /* Platforms and Versions information */
  ios: true,
  iosGameVersionFrom: "",
  iosGameVersionTo: "",
  android: true,
  androidGameVersionFrom: "",
  androidGameVersionTo: "",
  isShopAbTest: false,
});

const { formChanged, resetTracking: resetFormTracking } = useFormTracking(form);

const selectedAbType = computed({
  get: () => (form.isDynamic ? "Dynamic" : "Static"),
  set: (value) => {
    form.isDynamic = value === "Dynamic";
  },
});

const beforeCancel = async () => {
  if (formChanged.value) {
    return confirm("Do you really want to leave this form? All your modification will be lost...", {
      confirmButtonText: "Yes I'm sure",
    });
  }
  return true;
};

/**
 * Validate version format to avoid error when comparing with tmp incorrect values
 */
const isFromVersionLowerThanToVersion = (from?: string | null, to?: string | null): Error | undefined => {
  if (from && to && compareVersions.validate(from) && compareVersions.validate(to)) {
    if (!compareVersions.compare(from, to, "<=")) {
      return new Error("The first version in the range must be lower or equal than the second");
    }
  }
  return undefined;
};

const formRules: Record<string, FormRule[]> = {
  name: [{ required: true, message: "the name is required", trigger: "blur" }],
  description: [{ required: true, message: "the description is required", trigger: "blur" }],
  layerId: [
    {
      required: true,
      message: "select the layer you want to assign your abtest into",
      trigger: "blur",
    },
  ],
  segmentIds: [{ type: "array", required: true, message: "select at least one segment", trigger: "change" }],
  dynamicSegmentIds: [{ type: "array", required: true, message: "select at least one segment", trigger: "change" }],
  beginsAt: [
    {
      type: "string",
      validator(_: unknown, value: string, callback: (error?: Error) => void) {
        if (value && form.endsAt) {
          const beginsAt = parseJSON(value);
          const endsAt = parseJSON(form.endsAt);

          if (beginsAt >= endsAt) {
            return callback(new Error("The start date must be earlier than the end date."));
          }
        }
        return callback();
      },
    },
  ],
  iosGameVersionFrom: [
    {
      type: "string",
      validator(_: unknown, value: string, callback: (error?: Error) => void) {
        const errors = isFromVersionLowerThanToVersion(value, form.iosGameVersionTo);
        callback(errors);
      },
    },
  ],
  androidGameVersionFrom: [
    {
      type: "string",
      validator(_: unknown, value: string, callback: (error?: Error) => void) {
        const errors = isFromVersionLowerThanToVersion(value, form.androidGameVersionTo);
        callback(errors);
      },
    },
  ],
};

/**
 * If we have an abtest, lets fill the form with those values
 */
watch(
  [abtest, layers],
  ([value, layersValue]) => {
    if (value) {
      const {
        name,
        description,
        priority,
        beginsAt,
        endsAt,
        isImmutable,
        isDynamic,
        segments: segmentIds,
        dynamicSegmentIds,
        layer,
      } = value;
      form.name = name;
      form.description = description;
      /**
       * Getting the layer id selected: if none let's select the default layer id,
       * the last case is a fallback that we shouldn't go in (but if we do we'll have a form error anyway due to the server-side)
       */
      form.layerId = layer?.id ?? "";
      form.beginsAt = beginsAt ?? undefined;
      form.endsAt = endsAt ?? undefined;
      form.priority = priority;
      form.isImmutable = isImmutable;
      form.isDynamic = isDynamic;
      form.segmentIds = segmentIds.map(({ id }) => id);
      form.dynamicSegmentIds = dynamicSegmentIds;
      form.ios = value.platform.ios;
      form.iosGameVersionFrom = value.platform.iosGameVersionFrom;
      form.iosGameVersionTo = value.platform.iosGameVersionTo;
      form.android = value.platform.android;
      form.androidGameVersionFrom = value.platform.androidGameVersionFrom;
      form.androidGameVersionTo = value.platform.androidGameVersionTo;
      form.isShopAbTest = value.treatmentTypes?.includes(AbtestType.Shop) ?? false;

      /** We want start tracking changes after loading the data  */
      resetFormTracking();
    }

    if (form.layerId === "" && !!layersValue) {
      form.layerId = layersValue.find((l) => !!l?.default)?.id ?? "";
    }
  },
  {
    immediate: true,
  }
);

const onDateChanged = (fieldName: "beginsAt" | "endsAt", value?: Date | null) => {
  form[fieldName] = typeof value === "string" ? value : value?.toISOString() ?? "";
};

const onSegmentUpdate = async (value: string[]) => {
  const dynamicIds = form.dynamicSegmentIds ?? [];
  const isRemoving = value.length < dynamicIds.length;

  if (!isRemoving) {
    form.dynamicSegmentIds = value;
    return;
  }

  const removedSegment = dynamicIds.find((id) => !value.includes(id));

  const isUsedInTreatment = cohorts.value?.some((cohort) =>
    cohort.treatments.some((treatment) => treatment.segmentId === removedSegment)
  );

  if (isUsedInTreatment) {
    const confirmed = await confirm(
      "The segment ABC which you are trying to delete already has treatment(s) configured. Removing this segment will delete unpublished configuration created for this AB. Are you sure you want to delete it?",
      {
        confirmButtonText: "Yes, remove it",
      }
    );
    if (confirmed) {
      form.dynamicSegmentIds = form.dynamicSegmentIds?.filter((id) => id !== removedSegment);
    } else {
      form.dynamicSegmentIds = [...dynamicIds];
    }
  } else {
    form.dynamicSegmentIds = form.dynamicSegmentIds?.filter((id) => id !== removedSegment);
  }
};

/**
 * Save the A/B Test
 */
const saveAndBackToList = ref(false);

const onSaveAndBackToList = (callback: () => void) => {
  saveAndBackToList.value = true;
  callback();
};

const { mutate: save, loading: saveLoading } = useAbtestFormPageSaveMutation({});

/**
 * Renaming a running A/B test goes straight to the manager API (we are moving away
 * from the BFF): the cosmetic PATCH only carries the name.
 */
const renameLoading = ref(false);

const { mutate: createInAppPurchaseAbTest, loading: createLoading } = useIapAbtestFormPageSaveMutation({});

/**
 * If the save creates the abtest, we get the new object id in returns.
 * We have to pass this id to route. If the save is an update, we already
 * have the id from the route.
 */
const saveForm = async () => {
  let response;

  if (isCreatingIapAbTest) {
    const { name, description, priority, layerId, segmentIds } = form;

    response = await createInAppPurchaseAbTest({
      gameId: props.gameId,
      versionId: props.versionId,
      input: {
        name,
        description,
        layerId,
        platform: route.query.platform === "IOS" ? Platform.Ios : Platform.Android,
        segmentIds,
        priority,
        inAppPurchases: window.history.state.selectedParentIaps,
      },
    });
  } else if (isRunningABTest.value) {
    renameLoading.value = true;
    try {
      await client.patch(`/app/${props.gameId}/version/${props.versionId}/ab-tests/${props.abtestId}`, {
        name: form.name,
      });
      notifySuccess("Your operation was successful.", { duration: 5 * 1000 });
    } catch (error) {
      notifyError("The A/B test could not be renamed.", { duration: 15 * 1000 });
      return;
    } finally {
      renameLoading.value = false;
    }
  } else {
    const { isShopAbTest, ...casualABTest } = form;

    let abTestType = [AbtestType.Class];
    if (abtest.value?.treatmentTypes) {
      abTestType = abtest.value?.treatmentTypes;
    } else {
      abTestType = isShopAbTest ? [AbtestType.Shop] : [AbtestType.Class];
    }

    const formPayload = {
      ...casualABTest,
      treatmentTypes: abTestType,
    };

    response = await save({
      gameId: props.gameId,
      versionId: props.versionId,
      abtestId: props.abtestId,
      input: formPayload,
    });
  }
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  const data = useMutationResponse(response, (resp) => {
    if (resp?.saveAbtest) {
      return resp?.saveAbtest;
    }
    if (resp?.saveIapAbtest) {
      return resp?.saveIapAbtest;
    }

    if (resp?.saveOffersAbTest) {
      return resp?.saveOffersAbTest;
    }
    return null;
  });

  const abtestId = props.abtestId ? props.abtestId : data?.abtestId;

  /**
   * Build the next location route:
   *  - the cohorts list if the user has clicked on the "next" button
   *  - the abtests list if the user has clicked on "save & back to list" button
   */
  let nextRouteLocation: RouteLocationRaw;
  if (saveAndBackToList.value) {
    nextRouteLocation = {
      name: links.games.abtests.list,
      params: {
        gameId: props.gameId,
        versionId: props.versionId,
      },
    };
  } else {
    if (!abtestId) {
      throw new Error("unknown abtestId");
    }

    const isDynamicAb = form.isDynamic || abtest.value?.isDynamic;

    nextRouteLocation = {
      name: isDynamicAb ? links.games.abtests.dynamicTreatment : links.games.abtests.cohorts.list,
      params: {
        gameId: props.gameId,
        versionId: props.versionId,
        abtestId,
      },
    };
  }

  router.push(nextRouteLocation);
};

/**
 * Links
 */

const onFormCancel = () => {
  router.push({ name: links.games.abtests.list, params: { gameId: props.gameId, versionId: props.versionId } });
};
</script>

<style lang="scss">
.abtest-form-page {
  .vd-form__content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    row-gap: 2rem;

    .game-version.locked {
      pointer-events: none;
      opacity: 0.6;
    }

    .shop-abtest-field {
      display: flex;
      align-items: center;
      margin-bottom: 1rem;

      .p-checkbox {
        margin-bottom: 0;
      }

      .shop-abtest-label {
        margin-left: 0.5rem;
      }
    }

    .layers-section {
      display: flex;
      flex-direction: row;
      gap: 10px;
      align-items: flex-end;

      .vd-form-field {
        margin-bottom: 0;
      }
    }

    .advanced-settings-section {
      grid-column: span 2;
    }

    @include medium-screen {
      > section {
        grid-column: span 2;
      }
    }

    > section {
      grid-column: 1;
      display: flex;
      flex-direction: column;

      > .vd-form-field:not(:last-of-type) {
        margin-bottom: size("S");
      }
    }

    .section-title {
      margin-bottom: size("S");
    }

    .to {
      margin: 0 size("M");
    }
  }

  .el-select {
    width: 100%;
  }

  .immutable-section {
    display: flex;
    align-items: flex-end;

    .info-icon {
      fill: $light-blue;
      margin-left: size("S");
    }
  }

  .ab-test-type-section {
    p {
      font-size: 12px;
      margin-bottom: 0;
    }

    a {
      color: $light-blue;
      text-decoration: underline;
    }
  }

  .targeted-audience-section {
    .platform-title {
      margin: size("L") 0 size("ML") 0;
    }

    .platform-versions {
      max-width: 800px;

      .platform {
        display: flex;
        align-items: center;
        margin: size("M") 0;
        height: 50px;

        .name {
          min-width: 70px;
          margin-left: size("M");
          font-size: font("S");
        }

        .game-version {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          column-gap: 10px;
          width: 100%;
          animation: appear 0.4s ease;

          .to {
            margin: 0 size("M");
            font-size: font("S");
          }
        }
      }
    }
  }

  .abtest-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: size("L");

    .step-controls {
      display: flex;
      align-items: center;

      .vd-button {
        margin-left: size("M");
      }
    }
  }
}

@include medium-screen {
  .abtest-form-page {
    .vd-form__content {
      .name-field,
      .description-field {
        grid-column: span 2;
      }
    }
  }
}
</style>

<style>
.targeted-segments .el-select__tags span {
  display: flex !important;
  align-items: center;
  flex-wrap: nowrap;
}

.targeted-segments .el-tag {
  position: relative !important;
}

.targeted-segments .el-tag + .el-tag {
  margin-left: 24px !important;
}

.targeted-segments .el-tag + .el-tag::before {
  content: "OR";
  position: absolute;
  right: calc(100% + 6px);
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  font-weight: 600;
  opacity: 0.7;
  font-size: 10px;
  color: var(--vd-color-blue-gray-500);
}
</style>
